import type { ReactNode } from 'react';

/**
 * The one page header: optional badge row ABOVE the title, canonical h1,
 * muted subtitle, right-aligned action cluster. Pages render Breadcrumbs
 * separately above this inside their space-y-6 stack.
 *
 * By default the action cluster sits beside the title from `lg` up. Pass
 * `stackActions` when the subtitle carries a lot of metadata (e.g. several
 * URLs) that would otherwise fight the buttons for the same row — the actions
 * then always drop to their own row below, right-aligned.
 */
export function PageHeader({
  title,
  titleClassName = '',
  subtitle,
  badges,
  actions,
  stackActions = false,
}: {
  title: ReactNode;
  titleClassName?: string;
  subtitle?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  stackActions?: boolean;
}) {
  return (
    <header
      className={
        stackActions
          ? 'flex flex-col gap-4'
          : 'flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between'
      }
    >
      <div className="min-w-0">
        {badges && <div className="mb-2 flex flex-wrap items-center gap-2">{badges}</div>}
        <h1 className={`font-display text-xl font-bold tracking-tight text-ink ${titleClassName}`}>
          {title}
        </h1>
        {subtitle && <div className="mt-1 text-sm text-ink-muted">{subtitle}</div>}
      </div>
      {actions && (
        <div
          className={`flex flex-wrap items-center gap-2 ${
            stackActions ? 'justify-start sm:justify-end' : 'justify-end lg:flex-1'
          }`}
        >
          {actions}
        </div>
      )}
    </header>
  );
}
